import { createContext, useContext, useEffect, useRef, useState, type ReactNode } from 'react'

import { createAdminApi, type AdminConfig } from '../services/adminApi'
import { useUser } from './UserContext'

// AdminContext loads the admin-tier config (feature overrides, settings) on
// first render for admin users. Cookie-based auth (via UserContext) — no
// API key threading.

interface AdminContextType {
  config: AdminConfig | null
  isAuthenticated: boolean
  /**
   * @deprecated kept as a stable truthy sentinel ('cookie') so existing
   * page useEffect deps and `if (!apiKey) return` guards keep working
   * during the cookie-auth migration. Will be removed in a follow-up
   * once page code drops its apiKey references.
   */
  apiKey: string
  loadConfig: () => Promise<void>
  loadStatus: string
  loadError: string
}

const AdminContext = createContext<AdminContextType>(null!)

export function AdminProvider({ children }: { children: ReactNode }) {
  const { isAdmin } = useUser()
  const [config, setConfig] = useState<AdminConfig | null>(null)
  const [loadStatus, setLoadStatus] = useState('')
  const [loadError, setLoadError] = useState('')
  const autoLoaded = useRef(false)

  const isAuthenticated = isAdmin

  const api = createAdminApi()

  async function loadConfig() {
    try {
      setLoadError('')
      setLoadStatus('Loading...')
      const cfg = await api.getConfig()
      setConfig(cfg)
      setLoadStatus('Loaded')
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e)
      setConfig(null)
      setLoadStatus('')
      setLoadError(msg)
    }
  }

  useEffect(() => {
    if (isAdmin && !autoLoaded.current) {
      autoLoaded.current = true
      loadConfig()
    }
    if (!isAdmin) {
      setConfig(null)
      autoLoaded.current = false
    }
  }, [isAdmin]) // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <AdminContext.Provider value={{
      config,
      isAuthenticated,
      apiKey: 'cookie', // deprecated sentinel — see interface comment
      loadConfig,
      loadStatus,
      loadError,
    }}>
      {children}
    </AdminContext.Provider>
  )
}

export const useAdmin = () => useContext(AdminContext)
